import React, { useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import LocalFloristRoundedIcon from "@mui/icons-material/LocalFloristRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import BeachAccessRoundedIcon from "@mui/icons-material/BeachAccessRounded";
import WavesRoundedIcon from "@mui/icons-material/WavesRounded";
import BedRoundedIcon from "@mui/icons-material/BedRounded";
import { motion } from "framer-motion";
import { keyframes } from "@emotion/react";
import { useNavigate } from "react-router-dom";

const MotionBox = motion(Box);

const MAP_LINK = "https://maps.app.goo.gl/crcsf2Dm6kGKLTNf8";
const MAP_Q = encodeURIComponent("Hostel Bichón, Pichilemu, Chile");
const MAP_SRC = `https://www.google.com/maps?q=${MAP_Q}&z=16&hl=es&output=embed`;

export default function Hero2() {
  const navigate = useNavigate();

  const puntos = [
    { text: "Casa remodelada de 120 años de historia.", icon: <LocalFloristRoundedIcon />, color: "#49d0b0" },
    { text: "Habitaciones compartidas para viajeros.", icon: <WavesRoundedIcon />, color: "#16a357" },
    { text: "Habitaciones privadas para descansar mejor.", icon: <StarRoundedIcon />, color: "#cfc54a" },
    { text: "Habitaciones matrimoniales con baño privado.", icon: <BedRoundedIcon />, color: "#c72e64" },
    { text: "A pasos de la playa.", icon: <BeachAccessRoundedIcon />, color: "#27276d" },
    { text: "Estacionamiento privado propio.", icon: <StarRoundedIcon />, color: "#49d0b0" },
    { text: "Espacios comunes y patios con plantas.", icon: <LocalFloristRoundedIcon />, color: "#c72e64" },
  ];

  const gradientShift = useMemo(
    () => keyframes`
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    `,
    []
  );

  const reserveSx = useMemo(
    () => ({
      fontSize: { xs: "1rem", sm: "1.02rem" },
      borderRadius: 2,
      px: { xs: 3, sm: 3.2 },
      py: { xs: 1.2, sm: 1.25 },
      fontWeight: 700,
      textTransform: "none",
      color: "#fff",
      boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
      background: "linear-gradient(270deg, #49d0b0, #16a357, #c72e64)",
      backgroundSize: "600% 600%",
      animation: `${gradientShift} 3.2s ease infinite`,
      transition: "transform 220ms ease, box-shadow 220ms ease, filter 220ms ease",
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: "0 14px 34px rgba(0,0,0,0.24)",
        filter: "brightness(1.05)",
      },
    }),
    [gradientShift]
  );

  const greenBtnSx = {
    borderRadius: 2,
    fontWeight: 800,
    textTransform: "none",
    backgroundColor: "#49d0b0",
    color: "#fff",
    boxShadow: "0 10px 22px rgba(0,0,0,0.18)",
    transition: "transform 220ms ease, box-shadow 220ms ease, filter 220ms ease",
    "&:hover": {
      backgroundColor: "#2db193",
      transform: "translateY(-1px)",
      boxShadow: "0 14px 28px rgba(0,0,0,0.22)",
      filter: "brightness(1.03)",
    },
  };

  const outlinedGreenSx = {
    borderRadius: 2,
    fontWeight: 800,
    textTransform: "none",
    color: "#16a357",
    borderColor: "#16a357",
    "&:hover": {
      borderColor: "#16a357",
      backgroundColor: "rgba(22,163,87,0.10)",
      transform: "translateY(-1px)",
    },
    transition: "transform 220ms ease, background-color 220ms ease",
  };

  return (
    <Box
      component="section"
      id="instalaciones"
sx={{
  position: "relative",

  // sigue siendo “hero” acotado (no 100vh)
  minHeight: { xs: "auto", md: "70vh" },

  // ✅ ahora sí centrado vertical
  display: "flex",
  alignItems: "center",

  // ✅ padding simétrico (no se cae para abajo)
  py: { xs: 4, md: 4 },

  bgcolor: "#27276d",
  overflow: "hidden",
  scrollMarginTop: { xs: "84px", md: "96px" },
}}
    >
      {/* Fondo */}
      <Box aria-hidden sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at 20% 25%, rgba(73,208,176,0.48), transparent 46%)," +
              "radial-gradient(circle at 80% 30%, rgba(73,208,176,0.34), transparent 50%)," +
              "radial-gradient(circle at 65% 20%, rgba(207,197,74,0.22), transparent 44%)," +
              "radial-gradient(circle at 55% 85%, rgba(199,46,100,0.22), transparent 48%)," +
              "radial-gradient(circle at 10% 85%, rgba(22,163,87,0.18), transparent 52%)," +
              "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0))",
          }}
        />
      </Box>

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={3} alignItems="stretch" justifyContent="center">
          {/* Izquierda */}
          <Grid item xs={12} md={7}>
            <MotionBox
              component={Paper}
              elevation={0}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.55, ease: "easeOut" }}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                border: "1px solid rgba(255,255,255,0.15)",
                background: "rgba(255,255,255,0.96)",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: "#c72e64",
                  fontWeight: 800,
                  letterSpacing: 2,
                  fontFamily: '"Inter"',
                }}
              >
                Instalaciones
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 500,
                  color: "#27276d",
                  mt: 0.5,
                  fontFamily: '"DynaPuff", cursive',
                }}
              >
                Un punto cultural de descanso
              </Typography>

              <List dense sx={{ mt: 1.5, mb: 0, py: 0 }}>
                {puntos.map((p) => (
                  <ListItem key={p.text} sx={{ px: 0, py: 0.45 }}>
                    <ListItemIcon sx={{ minWidth: 36, color: p.color }}>
                      {p.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={p.text}
                      primaryTypographyProps={{
                        sx: { color: "rgba(0,0,0,0.75)", fontWeight: 650 },
                      }}
                    />
                  </ListItem>
                ))}
              </List>

              <Box sx={{ flex: 1 }} />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<InstagramIcon />}
                  sx={outlinedGreenSx}
                  onClick={() =>
                    window.open("https://www.instagram.com/hostalbichon.chile/?hl=es", "_blank")
                  }
                >
                  Ver en Instagram
                </Button>

                <Button variant="contained" sx={reserveSx} onClick={() => navigate("/reserve")}>
                  Reservar ahora
                </Button>
              </Stack>
            </MotionBox>
          </Grid>

          {/* Derecha */}
          <Grid item xs={12} md={5} id="ubicacion">
            <Stack spacing={2} sx={{ height: "100%" }}>
              <MotionBox
                component={Paper}
                elevation={0}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.55, ease: "easeOut", delay: 0.05 }}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: "1px solid rgba(255,255,255,0.15)",
                  background: "rgba(255,255,255,0.96)",
                }}
              >
                <Stack direction="row" spacing={1.3} alignItems="center">
                  <PlaceOutlinedIcon sx={{ color: "#49d0b0" }} />
                  <Typography
                    sx={{
                      fontWeight: 500,
                      color: "#111",
                      fontFamily: '"DynaPuff", cursive',
                    }}
                  >
                    Ubicación
                  </Typography>
                </Stack>

                <Typography sx={{ mt: 1, color: "text.secondary", fontWeight: 650 }}>
                  Av. Daniel Ortúzar 519, Pichilemu. A 300m de la playa
                </Typography>

                <Button
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2, ...greenBtnSx }}
                  onClick={() => window.open(MAP_LINK, "_blank")}
                >
                  Abrir en Google Maps
                </Button>
              </MotionBox>

              <MotionBox
                component={Paper}
                elevation={0}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.55, ease: "easeOut", delay: 0.1 }}
                sx={{
                  p: 0,
                  borderRadius: 3,
                  border: "1px solid rgba(255,255,255,0.15)",
                  overflow: "hidden",
                  flex: 1,
                  // ✅ evita que el mapa se coma altura en pantallas altas
                  minHeight: { xs: 280, md: 320 },
                }}
              >
                <Box
                  component="iframe"
                  title="Mapa Hostel Bichón"
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src={MAP_SRC}
                  style={{ display: "block" }}
                />
              </MotionBox>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
